import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import Footer from "../../globalCountries/countryhomepagescomponent/Footer";
import News from "../countryhomepagescomponent/News";
import globalStyles from "../../../global";
import { getResponsiveSize } from "../../../utils/responsive";
import FreeDeliveryBanner from "../countryhomepagescomponent/FreeDeliveryBanner";

const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL;

  const styles = {
    root: {
      display: "flex",
      flexDirection: "column",
      minHeight: "100vh",
    },
    heroContainer: {
      position: "relative",
      height: {
        xs: "280px",   // mobile
        sm: "440px",   // tablets
        md: "500px",   // laptops/desktops
      },
      width: "100%",
      overflow: "hidden",
      backgroundColor: {
        xs: "#e0e7d8", // Solid color on mobile
        sm: "transparent", // Transparent on tablet and up
      },
    },
    heroImage: {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
      transform: isRTL ? "scaleX(-1)" : "none",
    },
    heroContent: {
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: isRTL ? "flex-start" : "flex-end",
      px: { xs: 2, sm: 4, md: 6 },
      zIndex: 1,
      width: { md: "60%" },
      maxWidth: "1200px",
      marginLeft: isRTL ? 0 : "auto", // Change based on RTL
      marginRight: isRTL ? "auto" : 0, // Change based on RTL
    },
    heroTitle: {
      color: globalStyles.colors.army_green,

      fontFamily: globalStyles.fonts.primaryUAE,
      fontWeight: 400,
      lineHeight: "auto",
      letterSpacing: "1%",
      textTransform: "uppercase",
      fontSize: {
        md: "55px",
        sm: "36px",
        xs: "30px",
      },
      width: "100%",
      paddingLeft: isRTL ? 0 : { xs: 2, sm: 4 },
      paddingRight: isRTL ? { xs: 2, sm: 4 } : 0,
    },
    mainContent: {
      width: "100%",
      maxWidth: "1200px",
      mx: "auto",
      mt: 4,
      py: 6,
      px: {
        xs: 1,
        sm: 0,
      },
    },
    sectionTitle: {
      fontWeight: 600,
      fontSize: {
        md: "24px",
        sm: "22px",
        xs: "18px",
      },
      lineHeight: "auto",
      letterSpacing: "0%",
      color: globalStyles.colors.army_green,
      mb: 2,
      fontFamily: globalStyles.fonts.primary,
      textAlign: isRTL ? "right" : "left",
    },
    paragraph: {
      fontWeight: 400,
      fontSize: { sm: "20px", xs: "16px" },
      lineHeight: "28px",
      letterSpacing: "0%",
      color: globalStyles.colors.uclean_medium_gray,
      mb: 2,
      fontFamily: globalStyles.fonts.primary,
      textAlign: isRTL ? "right" : "left",
    },
    subtitle: {
      color: globalStyles.colors.army_green,
      fontSize: {
        xs: getResponsiveSize(16),
        sm: getResponsiveSize(18),
        md: getResponsiveSize(20),
      },
      fontFamily: globalStyles.fonts.primary,
      mt: 2,
      textAlign: isRTL ? "right" : "left",
      textShadow: "1px 1px 2px rgba(0, 0, 0, 0.3)",
    },
    companyInfo: {
      fontSize: {
        xs: getResponsiveSize(16),
        sm: getResponsiveSize(18),
      },
      fontWeight: 600,
      color: globalStyles.colors.army_green,
      mb: 3,
      textAlign: isRTL ? "right" : "left",
      fontFamily: globalStyles.fonts.primary,
    },
  };

  const renderSection = (section, hasParagraphs) => {
    let sectionTitle = "";
    let content;

    // Handle special sections differently
    switch (section) {
      case "intro":
        content = t("privacyPolicy.intro.text");
        break;
      case "importantNotes":
        sectionTitle = t("privacyPolicy.importantNotes.title");
        content = t("privacyPolicy.importantNotes.text");
        break;
      default:
        sectionTitle = t(`privacyPolicy.sections.${section}.title`);
        content = hasParagraphs
          ? t(`privacyPolicy.sections.${section}.paragraphs`, {
              returnObjects: true,
            })
          : t(`privacyPolicy.sections.${section}.text`);
    }

    return (
      <Box sx={{ mb: 4 }}>
        {sectionTitle && (
          <Typography sx={styles.sectionTitle}>{sectionTitle}</Typography>
        )}
        {Array.isArray(content) ? (
          content.map((paragraph, index) => (
            <Typography key={index} sx={styles.paragraph}>
              {paragraph}
            </Typography>
          ))
        ) : (
          <Typography sx={styles.paragraph}>{content}</Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.heroContainer}>
        <Box
          component="img"
          src={`${imageBaseUrl}privacypolicy_banner.svg`}
          alt=""
          sx={{
            ...styles.heroImage,
            display: { xs: "none", sm: "block" }, // Hide image on mobile
          }}
        />
        <Container
          maxWidth="1200px"
          sx={{ height: "100%", position: "relative" }}
        >
          <Box sx={styles.heroContent}>
            <Typography variant="h1" sx={styles.heroTitle}>
              {t("privacyPolicy.subtitle")}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container>
        <Box sx={styles.mainContent}>
          {/* Company Info */}
          {/* <Typography sx={styles.companyInfo}>
            {t("privacyPolicy.companyName")} - {t("privacyPolicy.brandName")}
          </Typography> */}

          {/* Introduction */}
          {renderSection("intro", false)}

          {/* Important Notes */}
          {renderSection("importantNotes", false)}

          {/* Main Sections */}
          {renderSection("dataCollection", true)}
          {renderSection("dataUsage", true)}
          {renderSection("cookies", false)}
          {renderSection("dataSharing", false)}
          {renderSection("externalLinks", false)}
          {renderSection("security", false)}
          {renderSection("optOut", false)}
          {renderSection("advertising", false)}
          {renderSection("consent", true)}
          {renderSection("grievanceOfficer", false)}
        </Box>
      </Container>
      <FreeDeliveryBanner />
      <News />
      <Box sx={{ height: { sm: "80px", xs: "20px" } }} />
      <Footer />
    </Box>
  );
};

export default PrivacyPolicy;
